/**
 * A person's custom status where Slack shows it: the emoji right after their name (sidebar DMs, message authors,
 * mention suggestions, member lists) with a tooltip on hover (emoji, text up to 2 lines, "Until …"), and the full
 * status in the DM header and profile card. Expired statuses vanish at the moment they run out, without a reload:
 * the workspace's status timer (WorkspaceState.statusAt) re-renders every emoji here. Core logic/profile statusDisplay.
 * Native: the same core function; iOS / Android show the emoji after the name and the status at the top of the profile
 * sheet (long-press), since touch has no hover.
 */
import type { UserStatus } from "@bundle/contracts";
import { Profile, type StatusDisplay } from "@bundle/core";
import { useContext, useSyncExternalStore } from "react";
import { WorkspaceContext } from "../context/AppContext.tsx";
import { useLocale, useTr } from "../i18n/I18nProvider.tsx";
import { StatusMark } from "../primitives/index.ts";

const noSubscribe = () => () => {};
const noStatusAt = () => undefined;

/** The viewer's time zone ("Until …" is in my zone, not theirs). */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

/** What to show for someone's status right now (undefined: nothing), re-rendering when any status runs out. */
export function useStatusDisplay(person: { status?: UserStatus } | undefined): StatusDisplay | undefined {
  const ws = useContext(WorkspaceContext)?.workspace;
  // Only `statusAt` (a number): a row re-renders when a status expires, not on every workspace change.
  useSyncExternalStore(ws?.subscribe ?? noSubscribe, ws === undefined ? noStatusAt : () => ws.getSnapshot().statusAt, noStatusAt);
  const locale = useLocale();
  // Outside a workspace (galleries, previews) there is no clock: nothing shows.
  if (person?.status === undefined || ws === undefined) return undefined;
  return Profile.statusDisplay(person, ws.deps.clock.now(), viewerTimeZone(), locale);
}

export type StatusEmojiProps = {
  /** The person (a Member, a DM row, a DM peer…): only `status` is read. */
  person: { status?: UserStatus } | undefined;
  /** sm 14px (message headers), md 16px (DM header); default 15px (rows). */
  size?: "sm" | "md";
  /** Hover shows the status card (default). Off where the text is already on screen (the DM header, menus). */
  tooltip?: boolean;
  testID?: string;
};

/** The emoji after a name, or nothing (no status, or it expired). */
export function StatusEmoji({ person, size, tooltip = true, testID }: StatusEmojiProps) {
  const tr = useTr();
  const d = useStatusDisplay(person);
  if (d === undefined) return null;
  return <StatusMark emoji={d.emoji} text={d.text} until={d.until === undefined ? undefined : tr(d.until)} label={tr(d.label)} size={size} tooltip={tooltip} testID={testID} />;
}
